/**
 * Utility for persisting database explorer expanded state in localStorage
 */

const STORAGE_KEY_PREFIX = 'mazpin_explorer_expanded_'

/**
 * Get the storage key for a connection's expanded state
 */
function getStorageKey(connectionId: string): string {
  return `${STORAGE_KEY_PREFIX}${connectionId}`
}

/**
 * Save expanded nodes state for a connection
 */
export function saveExpandedState(connectionId: string, expandedNodes: Set<string>): void {
  try {
    const storageKey = getStorageKey(connectionId)
    const expandedArray = Array.from(expandedNodes)
    localStorage.setItem(storageKey, JSON.stringify(expandedArray))
  } catch (error) {
    console.warn('Failed to save explorer expanded state:', error)
  }
}

/**
 * Load expanded nodes state for a connection
 */
export function loadExpandedState(connectionId: string): Set<string> {
  try {
    const storageKey = getStorageKey(connectionId)
    const stored = localStorage.getItem(storageKey)
    if (stored) {
      const parsed = JSON.parse(stored) as unknown
      const expandedArray = Array.isArray(parsed) ? (parsed.filter((v): v is string => typeof v === 'string')) : []
      return new Set(expandedArray)
    }
  } catch (error) {
    console.warn('Failed to load explorer expanded state:', error)
  }
  
  // Return default state with connection expanded
  return new Set([connectionId])
}

/**
 * Clear expanded state for a specific connection
 */
export function clearExpandedState(connectionId: string): void {
  try {
    const storageKey = getStorageKey(connectionId)
    localStorage.removeItem(storageKey)
  } catch (error) {
    console.warn('Failed to clear explorer expanded state:', error)
  }
}

/**
 * Clear all explorer expanded states (used on logout)
 */
export function clearAllExpandedStates(): void {
  try {
    const keysToRemove: string[] = []
    
    // Find all keys that match our prefix
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        keysToRemove.push(key)
      }
    }
    
    // Remove all matching keys
    keysToRemove.forEach((key) => { localStorage.removeItem(key) })
  } catch (error) {
    console.warn('Failed to clear all explorer expanded states:', error)
  }
}

/**
 * Clear all explorer expanded states (for testing purposes)
 * This is the same as clearAllExpandedStates but exported for test use
 */
export function clearExpandedStatesForTesting(): void {
  clearAllExpandedStates()
}